import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {useQuery} from "@tanstack/react-query";
import {useMemo} from "react";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {
    useOrganizationalUnitTemplates,
    useWorkspace,
    useWorkspaceUserTemplates as useUserTemplates,
} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOptionalGeography} from "~/contexts/geographyContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import {logCardBuilder} from "~/modules/activityTimeline/builder/logCardBuilder";
import {cardDataResolver} from "~/modules/activityTimeline/resolver/cardDataResolver";
import {timelineGenerator} from "~/modules/activityTimeline/timeline/timelineGenerator";
import type {
    ActivityTimelineResolverContext,
    LogCardData,
    TimelineDayGroup,
} from "~/modules/activityTimeline/types/activityTimeline.types";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {getEventLogs} from "~/utilities/apis/api";
import {getEventContainerCudLogs} from "~/utilities/apis/eventContainersApi";
import {uniqueUuid} from "@vesta/unique/unique";

export function useActivityTimelineData(journey: EventContainerWithEvents): {
    groups: Array<TimelineDayGroup>;
    logCards: Array<LogCardData>;
    status: "pending" | "error" | "success";
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
} {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {workspace, workspaceCapabilities, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const eventContainerTemplate = useRequiredEventContainerTemplate(
        journey.templateId,
    );
    const eventTemplates = useEventTemplates();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const geography = useOptionalGeography();

    const query = useQuery({
        queryKey: ["activityTimeline", workspace.id, journey.id],
        queryFn: async () => {
            const [eventContainerLogsResult, eventLogsResult] = await Promise.all([
                getEventContainerCudLogs({
                    accessToken: heimdallUser.accessToken,
                    filters: {
                        logic: "AND",
                        filters: [{
                            column: "id",
                            operator: "equals",
                            value: journey.id,
                        }],
                    },
                    limit: 9999,
                    offset: 0,
                    orderBy: [{column: "updatedAt", direction: "desc"}],
                    intellsysBaseUrl,
                    templateId: journey.templateId,
                    workspaceId: workspace.id,
                }),
                getEventLogs({
                    workspaceId: workspace.id,
                    offset: 0,
                    limit: 9999,
                    filterConfiguration: {
                        revisionId: uniqueUuid`activity-timeline-event-logs`,
                        lineItems: [
                            {
                                columnId: "eventContainerId",
                                operation: FilterOperation.equals,
                                value: journey.id,
                            },
                        ],
                    },
                    intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                }),
            ]);

            if (eventContainerLogsResult.success !== true) {
                throw eventContainerLogsResult.error;
            }
            if (eventLogsResult.success !== true) {
                throw eventLogsResult.error;
            }

            return {
                eventContainerLogs: eventContainerLogsResult.data,
                eventLogs: eventLogsResult.data,
            };
        },
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    const resolverContext: ActivityTimelineResolverContext | null = useMemo(() => {
        if (query.data === undefined) {
            return null;
        }
        return {
            eventContainer: journey,
            eventLogs: query.data.eventLogs,
            eventContainerLogs: query.data.eventContainerLogs,
            eventTemplates,
            eventContainerTemplate,
            users,
            userTemplates,
            organizationalUnits,
            organizationalUnitTemplates,
            platformUserDetailsByUserId,
            userDefinedObjectOptionsByTemplateId,
            geography: geography,
            workspaceSpecific: {
                tryAmaronLevelColumns: isAmaronWorkspace(workspaceCapabilities) === true,
                agentColumnIds: [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID],
                remarksColumnId:
                    eventContainerTemplate.configuration
                        .eventContainerDetailsPage.remarksColumnId,
                journeyUtmSourceColumnIds: isAmaronWorkspace(workspaceCapabilities)
                    ? [AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID]
                    : [],
                eventUtmSourceColumnIds: isAmaronWorkspace(workspaceCapabilities)
                    ? [
                          AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
                          AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
                          AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
                      ]
                    : [],
            },
        };
    }, [
        query.data,
        journey,
        eventTemplates,
        eventContainerTemplate,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        workspace,
        platformUserDetailsByUserId,
        userDefinedObjectOptionsByTemplateId,
        geography,
    ]);

    const logCards = useMemo(() => {
        if (resolverContext === null) {
            return [];
        }
        const activityCards = cardDataResolver(getAdditionalColumns, resolverContext);
        return logCardBuilder(getAdditionalColumns, activityCards, resolverContext, workspace);
    }, [getAdditionalColumns, resolverContext, workspace]);

    const groups = useMemo(() => timelineGenerator(logCards), [logCards]);

    return {
        groups,
        logCards,
        status: query.status,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error,
    };
}
